/**
 * Passkeys (WebAuthn). The API returns options and expects credentials in the standard WebAuthn JSON format
 * (base64url-encoded binary fields); modern browsers convert natively, older ones use the fallbacks here.
 */

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface JsonCredentialDescriptor {
  id: string;
  type: string;
  transports?: string[];
}

type PkcStatic = typeof PublicKeyCredential & {
  parseCreationOptionsFromJSON?: (json: unknown) => PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON?: (json: unknown) => PublicKeyCredentialRequestOptions;
};

export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window && !!navigator.credentials;
}

export function parseCreationOptions(json: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const native = (globalThis.PublicKeyCredential as PkcStatic | undefined)?.parseCreationOptionsFromJSON;
  if (native) return native(json);
  const user = json.user as { id: string; name: string; displayName: string };
  return {
    ...(json as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64UrlToBuffer(json.challenge as string),
    user: { ...user, id: base64UrlToBuffer(user.id) },
    excludeCredentials: ((json.excludeCredentials as JsonCredentialDescriptor[] | undefined) ?? []).map((c) => ({
      ...c,
      type: 'public-key' as const,
      id: base64UrlToBuffer(c.id),
    })) as PublicKeyCredentialDescriptor[],
  };
}

export function parseRequestOptions(json: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  const native = (globalThis.PublicKeyCredential as PkcStatic | undefined)?.parseRequestOptionsFromJSON;
  if (native) return native(json);
  return {
    ...(json as unknown as PublicKeyCredentialRequestOptions),
    challenge: base64UrlToBuffer(json.challenge as string),
    allowCredentials: ((json.allowCredentials as JsonCredentialDescriptor[] | undefined) ?? []).map((c) => ({
      ...c,
      type: 'public-key' as const,
      id: base64UrlToBuffer(c.id),
    })) as PublicKeyCredentialDescriptor[],
  };
}

/** Serialises navigator.credentials results to the WebAuthn JSON format. */
export function credentialToJson(credential: PublicKeyCredential): string {
  const withToJson = credential as PublicKeyCredential & { toJSON?: () => unknown };
  if (typeof withToJson.toJSON === 'function') return JSON.stringify(withToJson.toJSON());

  const response = credential.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const json: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      ...(response.attestationObject && {
        attestationObject: bufferToBase64Url(response.attestationObject),
        transports: response.getTransports?.() ?? [],
      }),
      ...(response.authenticatorData && {
        authenticatorData: bufferToBase64Url(response.authenticatorData),
        signature: bufferToBase64Url(response.signature),
        userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
      }),
    },
  };
  return JSON.stringify(json);
}
